import {Component, inject} from '@angular/core';
import {DatePipe} from '@angular/common';
import {GridModel} from 'src/app/shared/model/page-grid';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {Trip} from '../../model/trip';
import {SearchTripResponse} from '../../model/search-trip-response';
import {TripGridService} from '../../service/trip-grid.service';
import {TripExternalStatusTypeService} from '../../service/trip-external-status-type.service';
import {TripExternalStatusType} from '../../model/trip-external-status-type';
import {of} from 'rxjs';

@Component({
    selector: 'trip-list',
    templateUrl: './trip-list.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class TripListComponent {

  private datePipe = inject(DatePipe);
  private tripExterrnalStatusTypeService = inject(TripExternalStatusTypeService);

  protected readonly tripsGrid: GridModel<SearchTripResponse> = {
      gridDataTestId: 'trip-list',
      gridTitle: 'Trip Ledger',
      replayButton: true,
      singleColumnFilter: false,
      defaultSort: {
        direction: 'desc',
        active: 'dateUpdated'
      },
      columns: [
        {
          columnProperty: 'id',
          columnHeader: 'Trip Number',
          sort: true,
          disablePreventEventLink: true,
          style: () => ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }),
          linkEventFunction: (item:Trip)=> `Trip/${item?.id}`,
        },
        {
          columnProperty: 'tripName',
          columnHeader: 'Trip Name',
          sort: true,
          textLeft: true,
        },
        {
          columnProperty: 'externalNumber',
          columnHeader: 'Adx Quote Number',
          sort: true,
        },
        {
          columnProperty: 'externalReference',
          columnHeader: 'Record Locator',
          sort: true,
        },
        {
          columnProperty: 'tripExternalStatusType.displayName',
          columnHeader: 'Status',
          columnPropertyFunction: (trip: Trip) => trip.tripExternalStatusType?.displayName || '',
          sort: true,
        },
        {
          columnProperty: 'tripStartDate',
          columnHeader: 'Start Date',
          columnPropertyFunction: (trip: Trip) => this.datePipe.transform(trip.tripStartDate, 'M/d/y') || '',
          sort: true,
        },
        {
          columnProperty: 'tripEndDate',
          columnHeader: 'End Date',
          columnPropertyFunction: (trip: Trip) => this.datePipe.transform(trip.tripEndDate, 'M/d/y') || '',
          sort: true,
        },
        {
          columnProperty: 'dateCreated',
          columnHeader: 'Date Created',
          columnPropertyFunction: (trip: Trip) => this.datePipe.transform(trip.dateCreated, 'M/d/y') || '',
          sort: true,
        },
        {
          columnProperty: 'isCancelled',
          columnHeader: 'Cancelled',
          columnPropertyFunction: (trip: Trip) => trip.isCancelled ? 'Yes' : 'No',
          sort: true,
        },
        {
          columnProperty: 'isArchived',
          columnHeader: 'Archived',
          columnPropertyFunction: (trip: Trip) => trip.isArchived ? 'Yes' : 'No',
          sort: true,
        },
        {
          columnProperty: 'invoicesCount',
          columnHeader: 'Invoices Count',
          columnPropertyFunction: (trip: Trip) => trip.adxInvoiceCount?.toString() || '0',
        },
      ],
      filters: [
        {
          filterProperty: 'withInvoice',
          filterDisplayName: 'With Invoice',
          filterType: 'RADIOBUTTON',
          config: {
            radioButtonBoolean: true,
            radioButtonNotShowClearButton: false
          },
          filterResetValue: null,
        },
        {
          filterProperty: 'tripName',
          filterDisplayName: 'Trip Name',
          filterType: 'AUTOCOMPLETE',
          config: {
            autocompleteTableName: 'Trip',
            autocompleteColumnName: 'tripName',
            autocompleteEmitText: true
          }
        },
        {
          filterProperty: 'externalNumber',
          filterDisplayName: 'Adx Quote Number',
          filterType: 'AUTOCOMPLETE',
          config: {
            autocompleteTableName: 'Trip',
            autocompleteColumnName: 'externalNumber',
            autocompleteEmitText: true
          }
        },
        {
          filterProperty: 'fmsTripId',
          filterDisplayName: 'Fms Trip Number',
          filterType: 'AUTOCOMPLETE',
          config: {
            autocompleteTableName: 'Trip',
            autocompleteColumnName: 'Id',
            autocompleteEmitText: true
          }
        },
        {
          filterProperty: 'fmsInvoiceId',
          filterDisplayName: 'Fms Invoice Number',
          filterType: 'AUTOCOMPLETE',
          config: {
            autocompleteTableName: 'AdxInvoice',
            autocompleteColumnName: 'Id',
            autocompleteEmitText: true
          }
        },
        {
          filterProperty: 'externalReference',
          filterDisplayName: 'Record Locator',
          filterType: 'AUTOCOMPLETE',
          config: {
            autocompleteTableName: 'Trip',
            autocompleteColumnName: 'externalReference',
            autocompleteEmitText: true
          }
        },
        {
          filterProperty: 'externalStatusTypeId',
          filterDisplayName: 'Status',
          filterType: 'DROPDOWN',
          config: {
            dropdownOptions: this.tripExterrnalStatusTypeService.getAllTripExternalStatusTypes(),
            dropdownValueProp: 'id',
            dropdownDisplayWith: (item: TripExternalStatusType) => item.displayName
          }
        },
        {
          filterProperty: 'tripStartDateFrom',
          filterDisplayName: 'Trip Start From',
          filterType: 'DATE',
        },
        {
          filterProperty: 'tripStartDateTo',
          filterDisplayName: 'Trip Start To',
          filterType: 'DATE',
        },
        {
          filterProperty: 'tripEndDateFrom',
          filterDisplayName: 'Trip End From',
          filterType: 'DATE',
        },
        {
          filterProperty: 'tripEndDateTo',
          filterDisplayName: 'Trip End To',
          filterType: 'DATE',
        },
        {
          filterProperty: 'isCancelled',
          filterDisplayName: 'Cancelled',
          filterType: 'DROPDOWN',
          config: {
            dropdownOptions: of([
              { value: 'true', display: 'Yes' },
              { value: 'false', display: 'No' },
            ]),
            dropdownValueProp: 'value',
            dropdownDisplayWith: (item: any) => item.display
          }
        },
        {
          filterProperty: 'isArchived',
          filterDisplayName: 'Archived',
          filterType: 'DROPDOWN',
          config: {
            dropdownOptions: of([
              { value: 'true', display: 'Yes' },
              { value: 'false', display: 'No' }
            ]),
            dropdownValueProp: 'value',
            dropdownDisplayWith: (item: any) => item.display
          }
        },
    ],
      filtersInitial: { withInvoice: true },
      gridGetService: inject(TripGridService)
  };
}
